import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Inject,
  Input,
} from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type {
  OnlineOfflineCollection,
  Thread,
  ThreadContext,
  ThreadsCollection,
} from '@freelancer/datastore/collections';
import {
  ThreadContextType,
  ThreadType,
} from '@freelancer/datastore/collections';
import { MessagingChat } from '@freelancer/messaging-chat';
import { ButtonColor, ButtonDisplay, ButtonSize } from '@freelancer/ui/button';
import { OnlineIndicatorSize } from '@freelancer/ui/online-indicator';
import { FontColor, FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { ThreadTypeApi } from 'api-typings/messages/messages_types';
import { OnlineOfflineStatusApi } from 'api-typings/users/users';
import type { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'fl-chat-button',
  template: `
    <ng-template
      #button
      let-thread="thread"
    >
      <fl-button
        class="ChatButton"
        [attr.data-animate]="!!(unreadMessagesNumber$ | flAsync)"
        [attr.full-width]="fullWidth"
        [color]="color"
        [disabled]="disabled"
        [display]="display"
        [displayTablet]="displayTablet"
        [displayDesktop]="displayDesktop"
        [size]="size"
        (click)="chat(thread?.id)"
      >
        <div class="ChatButton-wrapper">
          <fl-online-indicator
            class="ChatButton-content"
            [isOnline]="(isOnline$ | flAsync) === true"
            [flMarginRight]="'xxsmall'"
            [size]="onlineIndicatorSize"
          ></fl-online-indicator>

          <span
            class="text-color-inherit text-size-inherit ChatButton-content font-bold"
          >
            <ng-container
              *ngIf="uiConfig.theme === 'deloitte'; else defaultVerb"
              i18n="Message button (verb)"
            >
              Message
            </ng-container>

            <ng-template #defaultVerb>
              <ng-container>
                {{ buttonText }}
              </ng-container>
            </ng-template>
          </span>

          <fl-text
            *ngIf="unreadMessagesNumber$ | flAsync as unReadNumber"
            class="ChatButton-content"
            [attr.data-notification-size]="notificationSize"
            [class.UnreadCount]="true"
            [size]="notificationSize"
            [fontType]="FontType.SPAN"
            [weight]="FontWeight.BOLD"
            [color]="FontColor.LIGHT"
          >
            {{ unReadNumber < 100 ? unReadNumber : '99' }}
          </fl-text>
        </div>
      </fl-button>
    </ng-template>

    <ng-container
      *ngTemplateOutlet="button; context: { thread: thread$ | flAsync }"
    ></ng-container>
  `,
  styleUrls: ['./chat-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatButtonComponent implements OnInit, OnChanges {
  TextSize = TextSize;
  FontType = FontType;
  FontWeight = FontWeight;
  FontColor = FontColor;
  ButtonSize = ButtonSize;

  /**
   * Other members of the chat.
   * The online indicator will use the first member in the list
   */
  @Input() otherMemberIds: readonly number[];

  /**
   * Override thread type where the default value is
   * PRIVATE if otherMembers length is 1 or
   * GROUP if otherMembers length is greater than 1.
   */
  @Input() threadType?: ThreadType;
  @Input() buttonText?: string = $localize`:Chat button (verb):Chat`;
  @Input() contextType: ThreadContextType;
  @Input() contextId: number;
  @Input() disabled?: boolean = false;
  @Input() color = ButtonColor.DEFAULT;

  @HostBinding('attr.data-display')
  @Input()
  display: ButtonDisplay = 'inline';
  @HostBinding('attr.data-display-tablet')
  @Input()
  displayTablet?: ButtonDisplay;
  @HostBinding('attr.data-display-desktop')
  @Input()
  displayDesktop?: ButtonDisplay;
  /**
   * By default the chat button has a fixed size,
   * setting this to true fits the button to the
   * container width
   */
  @Input()
  fullWidth = false;

  /** Size of the button. Automatically adjusts font/notification size. */
  @Input() size = ButtonSize.SMALL;
  notificationSize = TextSize.XXSMALL;
  onlineIndicatorSize = OnlineIndicatorSize.SMALL;

  isOnline$: Observable<boolean>;
  thread$: Observable<Thread | undefined>;
  unreadMessagesNumber$: Observable<number | false>;

  constructor(
    private datastore: Datastore,
    private messagingChat: MessagingChat,
    @Inject(UI_CONFIG) public uiConfig: UiConfig,
  ) {}

  ngOnInit(): void {
    this.setupOtherMembers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('size' in changes) {
      switch (this.size) {
        case ButtonSize.MINI:
          this.notificationSize = TextSize.XXXSMALL;
          this.onlineIndicatorSize = OnlineIndicatorSize.SMALL;
          break;
        case ButtonSize.SMALL:
          this.notificationSize = TextSize.XXSMALL;
          this.onlineIndicatorSize = OnlineIndicatorSize.SMALL;
          break;
        case ButtonSize.LARGE:
          this.notificationSize = TextSize.XSMALL;
          this.onlineIndicatorSize = OnlineIndicatorSize.LARGE;
          break;
        case ButtonSize.XLARGE:
          this.notificationSize = TextSize.SMALL;
          this.onlineIndicatorSize = OnlineIndicatorSize.XXLARGE;
          break;
        case ButtonSize.XXLARGE:
          this.notificationSize = TextSize.MID;
          this.onlineIndicatorSize = OnlineIndicatorSize.SMALL;
          break;
        default:
          this.notificationSize = TextSize.XXSMALL;
          this.onlineIndicatorSize = OnlineIndicatorSize.SMALL;
      }
    }

    if (
      ('otherMemberIds' in changes || 'threadType' in changes) &&
      this.otherMemberIds
    ) {
      this.setupOtherMembers();
    }
  }

  chat(threadId: number | undefined): void {
    this.messagingChat.startChat({
      threadId,
      userIds: this.otherMemberIds,
      threadType: this.getThreadType(),
      origin: '', // unused, even by the messaging chat component
      context: this.getThreadContext(),
    });
  }

  private setupOtherMembers(): void {
    this.isOnline$ = this.datastore
      .document<OnlineOfflineCollection>(
        'onlineOffline',
        this.otherMemberIds[0],
      )
      .valueChanges()
      .pipe(
        map(status => status.status === OnlineOfflineStatusApi.ONLINE),
        startWith(false),
      );

    this.thread$ = this.datastore
      .collection<ThreadsCollection>('threads', query =>
        this.contextType && this.contextId
          ? query
              .where('otherMembers', 'equalsIgnoreOrder', this.otherMemberIds)
              .where('context', '==', this.getThreadContext())
              .where('threadType', '==', this.getThreadType())
          : query
              .where('otherMembers', 'equalsIgnoreOrder', this.otherMemberIds)
              .where('threadType', '==', this.getThreadType()),
      )
      .valueChanges()
      .pipe(map(a => a[0]));

    this.unreadMessagesNumber$ = this.thread$.pipe(
      map(thread => !!thread && thread.messageUnreadCount),
    );
  }

  private getThreadType(): ThreadType {
    if (this.threadType) {
      return this.threadType;
    }

    return this.otherMemberIds.length === 1
      ? ThreadTypeApi.PRIVATE_CHAT
      : ThreadTypeApi.GROUP;
  }

  private getThreadContext(): ThreadContext {
    return {
      type: this.contextType,
      id: this.contextId,
    };
  }
}
